import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { User, Mail, Lock, Phone, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const UserAuth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "client" });
  const [loading, setLoading] = useState(false);
  const { login, register } = useUserAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(form.email, form.password);
        toast.success("Welcome back!");
      } else {
        await register(form);
        toast.success("Account created successfully!");
      }
      navigate("/portal");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join(" ")
        : "Something went wrong";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-warm-50/30 to-slate-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-warm-700 mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to website
        </Link>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <div className="text-center mb-8">
            <div className="w-14 h-14 bg-warm-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <User className="w-7 h-7 text-warm-700" />
            </div>
            <h1 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>
              {isLogin ? "Welcome Back" : "Create Account"}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {isLogin ? "Sign in to your portal" : "Join The Pillar Project community"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <Label htmlFor="name" className="text-sm font-medium text-gray-700">Full Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="name"
                      data-testid="user-register-name"
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      placeholder="Your full name"
                      required
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium text-gray-700">Phone</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="phone"
                      data-testid="user-register-phone"
                      value={form.phone}
                      onChange={e => setForm({ ...form, phone: e.target.value })}
                      placeholder="Phone number"
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">I am a...</Label>
                  <div className="flex gap-3 mt-1">
                    {[{ value: "client", label: "Client / Participant" }, { value: "volunteer", label: "Volunteer" }].map(r => (
                      <button
                        key={r.value}
                        type="button"
                        data-testid={`role-${r.value}`}
                        onClick={() => setForm({ ...form, role: r.value })}
                        className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium border transition-all ${
                          form.role === r.value
                            ? "border-warm-700 bg-warm-50 text-warm-700"
                            : "border-gray-200 text-gray-500 hover:border-gray-300"
                        }`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div>
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  data-testid="user-email"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="you@example.com"
                  required
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  data-testid="user-password"
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder="Enter password"
                  required
                  className="pl-10"
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid="user-auth-submit"
              disabled={loading}
              className="w-full bg-warm-700 hover:bg-warm-800 text-white rounded-xl py-3 mt-2"
            >
              {loading ? "Please wait..." : isLogin ? "Sign In" : "Create Account"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              data-testid="user-auth-toggle"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-warm-700 hover:text-warm-800 font-medium"
            >
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserAuth;
